import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch, getPaymentConfig, apiFetchOk } from '../utils/api';
import { formatKrw } from '../utils/currency';
import PaymentMethodOptions from '../components/PaymentMethodOptions';
import BuyerPhoneField from '../components/BuyerPhoneField';
import './ManageMembershipPage.css';
import { CHECKOUT_WITHDRAWAL_NOTICE, REFUND_POLICY_SUMMARY } from '../constants/legal';
import { PLAN_MONTHLY_CREDITS, TRIAL_SIGNUP_CREDITS, monthlyCreditsLine, planTermTotalLine } from '../constants/plans';

const WIRE = {
  bank: '국민은행 (Kookmin Bank)',
  name: '심상미(웨어엣)',
  account: '371101-04-175346',
};

// Release A §10.2-A2 — THE SUBMIT BRANCH IS DRIVEN BY THIS MAP, NOT BY `=== 'card'`.
//
// Before this existed the submit was binary: `payMethod === 'card' ? SDK : wireRequest`.
// Adding a 계좌이체 button without changing that would have sent anyone choosing
// 계좌이체 down the MANUAL wire path — no payment window, nothing charged,
// membership left pending, and no error shown. Any new PortOne method must be
// added here and nowhere else.
//
// Values are PortOne V2 `payMethod` enums.
export const PG_METHODS = {
  card:     'CARD',
  transfer: 'TRANSFER',   // 실시간 계좌이체 — settles synchronously, exactly like card
};
// hasOwnProperty, not `in`: `'constructor' in PG_METHODS` is true, which would
// route an unknown method to a payment window with payMethod undefined.
export const isPgMethod = (m) => Object.prototype.hasOwnProperty.call(PG_METHODS, m);

const STEP_SELECT       = 'select';
const STEP_DETAILS      = 'details';
const STEP_DONE         = 'done';

function addMonths(d, n) {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}

function toIso(d) {
  return d.toISOString().split('T')[0];
}

// Korean mobile number, hyphens optional. Inicis wants digits; we strip them.
export const PHONE_DIGITS = (v) => (v || '').replace(/[^0-9]/g, '');
export const isValidKrMobile = (v) => /^01[016789][0-9]{7,8}$/.test(PHONE_DIGITS(v));

// formatKrw moved to utils/currency.js when the pack surfaces shared it. They
// are gone now, but it stays there: one currency formatter, one place.

// Canonical FULL 12-MONTH PREPAID prices — fallback if DB price_krw is 0.
// (PortOne-Payment-Refactor-8-14.md P1.) These are TERM TOTALS, not monthly
// rates: the multiply-by-chosen-duration model was removed along with the
// calendar. `team` was renamed to `pro`; `studio` retired.
const TIER_PRICES = {
  trial:   0,
  starter: 2388000,   // ₩199,000 × 12
  pro:     5988000,   // ₩499,000 × 12
};

// Monthly analysis-report allowance: imported, never re-typed. See
// constants/plans.js.
//
// This file used to hold its own `const TIER_MONTHLY_CREDITS = {starter:200,
// pro:600}` -- a FOURTH hand-written copy of a number that also lived in
// PricingSection, PricingPage and the database. On THIS screen, which is where
// the buyer commits 2,388,000 won, a stale copy would quote an allowance the
// grant does not deliver. That is the shape of the incident in
// .claude/TODO/Credit-manage-analysis-9-23.md: a plan advertised as 200/month
// that paid out 2, which section 10 of that document calls a mis-sale.
//
// backend/tests/test_plan_credits_match_frontend.py parses constants/plans.js
// and asserts it equals product_catalogue.monthly_credits_for_tier, so the
// number quoted at checkout is now tethered to the number the anniversary
// grant actually writes.

// Tier name → sellable plan SKU (backend product_catalogue.CATALOGUE).
//
// /checkout/session still accepts the legacy `tier_name`, but
// /checkout/preview-coupon prices a SKU and takes ONLY `product_code`
// (promotions.py CouponPreviewBody), so the mapping has to exist on this side.
// It carries NO price — the catalogue is still the only place a plan is priced.
// A tier absent from this map (trial, and anything retired) has no purchasable
// SKU, so it gets no coupon field rather than a request that would 404.
const PLAN_PRODUCT_CODES = {
  starter: 'plan_starter_12m',
  pro:     'plan_pro_12m',
};

// Tiers no longer offered for sale. Existing memberships on them stay valid to
// their end_date (Q6, "allowed to run out"), so they may still appear as a
// CURRENT plan — they just cannot be chosen.
const RETIRED_TIERS = new Set(['team', 'studio', 'fgi_studio']);

// ── Credit packs: WITHDRAWN FROM SALE 2026-09-03 ───────────────────────
// Refund-process-refactor-9-2.md T0.1a (Q1). The section, its fallback
// price table, the /public/credit-packs fetch and the pack checkout step
// were all removed together: `pack_3` and `pack_50` are `sellable=False` in
// the server catalogue, /public/credit-packs now returns an empty list, and
// POST /payments/checkout/pack answers 410. A price table with no route to
// spend it is worse than nothing.
//
// The STEP_DONE pack confirmation was kept at first, for a mobile PortOne
// redirect returning from a purchase started before the withdrawal. It was
// deleted on 2026-10-03 with its redirect mapping and its CSS
// (Portone-KGinisys-Pricing-fix-10-2.md T3.1, D-8): a month on, no in-flight
// pack purchase can remain, no new one can be started, and the screen's copy
// described a never-expiring stored value -- the wording KG이니시스 reviews
// as 환금성.
// ────────────────────────────────────────────────────────────────────────

function priceOf(tier) {
  if (!tier) return 0;
  return tier.price_krw > 0 ? tier.price_krw : (TIER_PRICES[tier.name] ?? 0);
}

function monthlyRateOf(tier) {
  const total = priceOf(tier);
  return total > 0 ? Math.round(total / 12) : 0;
}

// calcMonths() removed with the calendar (P2): nothing computes a duration
// from user-chosen dates any more — the term is fixed at 12 months and the
// price is the catalogue's term total.

export default function ManageMembershipPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  const [step, setStep]       = useState(STEP_SELECT);
  const [tiers, setTiers]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]   = useState('');
  const [loading, setLoading] = useState(false);
  // ← Back must return to WHERE THE USER CAME FROM, not to a fixed page.
  //
  // This navigated to '/profile' unconditionally, but five places link here:
  // the dashboard card, the credit-exhausted modal, Settings > 구독, and two
  // buttons on the profile page. For three of those, 'Back' silently moved the
  // user somewhere they had not been — the reported case is Settings, where it
  // dumps you on the profile and loses the tab you were on.
  //
  // `window.history.state.idx` is React Router's own history index. > 0 means
  // there is an in-app entry to go back to; 0 means this page was opened
  // directly (pasted link, new tab, refresh) and navigate(-1) would leave the
  // app entirely. The profile stays as the fallback for exactly that case.
  const goBack = useCallback(() => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/profile');
  }, [navigate]);

  const [error, setError]     = useState('');
  const [result, setResult]   = useState(null);
  const [copied, setCopied]   = useState('');
  const [pendingTier, setPendingTier] = useState(null); // waiting for change-plan confirmation
  const [currentMembership, setCurrentMembership] = useState(null);
  const [payCfg, setPayCfg] = useState({ card_enabled: false, store_id: '', channel_key: '', report_price_krw: 0 });
  // Release A (Portone-Credit-card-window-payment-default-8-22.md §10.2).
  // 'card' | 'transfer'  → PG methods, settle instantly through the PortOne window
  // 'wire'               → manual 무통장 입금; an admin activates it later
  const [payMethod, setPayMethod] = useState('wire');
  // KG Inicis V2 일반결제 REQUIRES the buyer's mobile number. PortOne's
  // /api/prepare/v2 rejects the whole request without it:
  //   "이니시스 V2 일반 결제의 경우 구매자 휴대폰 번호는 필수 입력입니다."
  // We hold no phone number anywhere — there is no column for one in `users`
  // — so it is collected here and passed STRAIGHT THROUGH to PortOne. It is
  // deliberately NOT persisted: storing it would add PII at rest (and a
  // migration) for a value only the PG needs.
  const [buyerPhone, setBuyerPhone] = useState('');

  // ── COUPON ────────────────────────────────────────────────────────────────
  // ONE server quote, or nothing at all. The browser never works out a
  // discounted price: it asks /checkout/preview-coupon what this SKU costs with
  // this code and renders the answer verbatim. A number computed here would be
  // a number the customer is SHOWN while the server charges a different one.
  const [couponInput,  setCouponInput]  = useState('');
  const [couponQuote,  setCouponQuote]  = useState(null);  // server preview, or null
  const [couponNotice, setCouponNotice] = useState('');    // rejection text, straight from the server
  const [couponBusy,   setCouponBusy]   = useState(false);

  const selectedTierName  = selected ? selected.name : null;
  const couponProductCode = selectedTierName ? (PLAN_PRODUCT_CODES[selectedTierName] || null) : null;

  // A quote is trusted ONLY for the SKU it was issued for. This is a
  // RENDER-TIME guard, not cleanup: switching plans re-renders before any
  // effect runs, so without it there is a frame where the new plan is shown at
  // the previous plan's discounted total. The effect below clears the input and
  // message so the rest of the UI agrees with this.
  const couponApplied =
    couponQuote && couponQuote.product_code === couponProductCode ? couponQuote : null;

  // `today` (the date pickers' `min=`) went away with the calendar (P2).

  // Handle tier card click — confirm if switching away from an already-selected plan
  const handleTierClick = (tier) => {
    if (selected && selected.name !== tier.name) {
      setPendingTier(tier);
    } else {
      setSelected(tier);
    }
  };

  const confirmTierChange = () => {
    const newTier = pendingTier;
    setSelected(newTier);
    setPendingTier(null);
    // Reset dates for fresh calculation
    const start = new Date();
    start.setDate(start.getDate() + 1);
    setStartDate(toIso(start));
    setEndDate(toIso(addMonths(start, 12)));
    setStep(STEP_DETAILS);
  };

  // Load tiers on mount + current membership
  useEffect(() => {
    apiFetch('/api/v1/public/membership-tiers')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setTiers(data.tiers || data))
      .catch(() => setError('Failed to load pricing tiers.'));

    apiFetch('/api/v1/profile/membership')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.has_membership) setCurrentMembership(data.membership); })
      .catch(() => {});

    // Phase 5 — show the PG options only when the backend flag + keys are present.
    // T1.4: card is the DEFAULT whenever PG payment is available, and wire is the
    // fallback. Set from the fetched config in one place so the intent is
    // readable rather than being a side effect somewhere else.
    getPaymentConfig().then(cfg => {
      setPayCfg(cfg);
      setPayMethod(cfg.card_enabled ? 'card' : 'wire');
    });
  }, []);

  // Pre-select tier from URL param
  useEffect(() => {
    const tierParam = searchParams.get('tier');
    if (tierParam && tiers.length > 0) {
      const match = tiers.find(t => t.name.toLowerCase() === tierParam.toLowerCase());
      if (match) setSelected(match);
    }
  }, [tiers, searchParams]);

  // ── T0.4 — MOBILE REDIRECT RETURN HANDLER ────────────────────────────────
  //
  // `redirectUrl` was already being passed to requestPayment, but NOTHING read
  // the params it comes back with, so the mobile flow was only half wired: KG
  // Inicis navigates the browser away, this component unmounts, and the awaited
  // requestPayment promise never resolves — so /checkout/complete was never
  // called. The buyer was charged and returned to a page that acted as though
  // nothing had happened. (The webhook still settles server-side, which is why
  // this failed quietly rather than losing money.)
  //
  // On return PortOne appends paymentId, and code/message when it failed.
  useEffect(() => {
    const paymentId = searchParams.get('paymentId');
    if (!paymentId) return;

    const failCode = searchParams.get('code');
    if (failCode) {
      setError(searchParams.get('message') || (isKo ? '결제가 완료되지 않았습니다.' : 'Payment was not completed.'));
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch('/api/v1/payments/checkout/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payment_id: paymentId }),
        });
        const done = await res.json();
        if (cancelled) return;
        if (res.ok && done.membership_active) {
          // Plans only. The credit-pack mapping that branched here on
          // `done.product_type` went with the pack confirmation screen
          // (2026-10-03, T3.1): pack checkout has answered 410 since
          // 2026-09-03, so no new payment can come back as a pack.
          setResult({ paid: true, amount_due_krw: done.amount_krw });
          setStep(STEP_DONE);
        } else {
          // The webhook is the backstop, so "not yet" is not an error state.
          setError(done.detail || (isKo
            ? '결제가 확인되는 중입니다. 잠시 후 새로고침해 주세요.'
            : 'Payment received — activation is finishing. Please refresh shortly.'));
        }
      } catch {
        if (!cancelled) {
          setError(isKo ? '결제 확인 중 오류가 발생했습니다.' : 'Could not confirm the payment.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, isKo]);

  // Default dates when tier selected
  useEffect(() => {
    if (selected && !startDate) {
      const start = new Date();
      start.setDate(start.getDate() + 1); // tomorrow
      setStartDate(toIso(start));
      setEndDate(toIso(addMonths(start, 12)));
    }
  }, [selected]);

  // A coupon may be scoped to a single SKU, so a quote issued for one plan must
  // not survive onto another — the buyer would otherwise see Pro at Starter's
  // discount and then be charged Pro's list price by the server.
  //
  // Keyed on the tier NAME, not the tier object: a refetch of
  // /public/membership-tiers hands back an equal-but-new object, which would
  // wipe a code the buyer had only just applied.
  useEffect(() => {
    setCouponQuote(null);
    setCouponInput('');
    setCouponNotice('');
  }, [selectedTierName]);

  // priceOf() now returns the FULL 12-month prepaid total (P1). The old
  // "monthly rate x user-chosen months" derivation went away with the
  // calendar (P2) - the buyer no longer picks a duration, so
  // handleSetDuration() and the +1mo/+2yr buttons are gone with it.
  const priceKrw = priceOf(selected);
  const tierPricePerMonth = monthlyRateOf(selected);

  // WHAT THE CUSTOMER IS ACTUALLY CHARGED. With a coupon this is the SERVER's
  // net (preview-coupon → amount_krw); with no coupon it is the catalogue price
  // as before. Every total, button label and disable guard below reads THIS and
  // never `priceKrw` — showing the list price while a different amount is
  // charged is the exact failure this is here to prevent.
  const payableKrw = couponApplied ? couponApplied.amount_krw : priceKrw;
  // The code the SERVER echoed back for this SKU, never the raw input box.
  const appliedCouponCode = couponApplied ? couponApplied.coupon_code : null;

  const applyCoupon = useCallback(async () => {
    // normalize_code() on the server folds to trimmed upper case; do the same
    // here so a phone keyboard's auto-capitalisation is not a rejection.
    const code = (couponInput || '').trim().toUpperCase();
    if (!code || !couponProductCode || couponBusy) return;
    setCouponBusy(true);
    setCouponNotice('');
    try {
      const res = await apiFetch('/api/v1/payments/checkout/preview-coupon', {
        method: 'POST',
        body: JSON.stringify({ code, product_code: couponProductCode }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) {
        // Transport / feature-disabled / malformed — NOT a verdict on the code.
        setCouponQuote(null);
        setCouponNotice(t('coupon.checkFailed'));
        return;
      }
      if (!data.valid) {
        // *** AN UNUSABLE CODE IS A NORMAL 200 WITH valid:false. *** Unknown,
        // expired, revoked, exhausted and wrong-SKU all come back with the SAME
        // message on purpose (coupon_service.GENERIC_REJECTION): distinct
        // wording would confirm that a code EXISTS, which is the oracle an
        // enumeration attack needs. Render the server's sentence as-is and do
        // not add a reason of our own.
        setCouponQuote(null);
        setCouponNotice(data.message || t('coupon.checkFailed'));
        return;
      }
      // `amount_krw` becomes the total, the button label AND the disable guard,
      // so a missing or non-numeric field must not slip through as ₩0 next to an
      // enabled Pay button. Reject the payload rather than render a price the
      // server never quoted.
      const listAmt = Number(data.list_amount_krw);
      const discAmt = Number(data.discount_krw);
      const netAmt  = Number(data.amount_krw);
      if (![listAmt, discAmt, netAmt].every(n => Number.isFinite(n) && n >= 0)) {
        setCouponQuote(null);
        setCouponNotice(t('coupon.checkFailed'));
        return;
      }
      setCouponQuote({
        product_code:    couponProductCode,
        coupon_code:     data.coupon_code || code,
        list_amount_krw: listAmt,
        discount_krw:    discAmt,
        amount_krw:      netAmt,
      });
      setCouponNotice('');
    } catch {
      setCouponQuote(null);
      setCouponNotice(t('coupon.checkFailed'));
    } finally {
      setCouponBusy(false);
    }
  }, [couponInput, couponProductCode, couponBusy, t]);

  const clearCoupon = useCallback(() => {
    setCouponQuote(null);
    setCouponInput('');
    setCouponNotice('');
  }, []);

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const handleSubmit = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch('/api/v1/profile/membership/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier_name: selected.name.toLowerCase(),
          start_date: startDate,
          end_date: endDate,
          // Coupons apply to the wire path too (profile.py MembershipRequestBody).
          // Only the CODE crosses the wire — the server re-quotes it through the
          // same CouponService the card path uses and returns the net as
          // `amount_due_krw`, which is the figure the buyer is told to transfer.
          // Omitting this would file the request at the LIST price after the
          // buyer was shown a discounted total.
          ...(appliedCouponCode ? { coupon_code: appliedCouponCode } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Submission failed. Please try again.');
      } else {
        setResult(data);
        setStep(STEP_DONE);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selected, startDate, endDate, appliedCouponCode]);

  // Phase 5 — instant card payment via the PortOne (KCP) window.
  const handleCardPayment = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      // 1. Create the payment intent server-side.
      // No dates: the service period is derived server-side from the SKU
      // (P2-T2.2). `tier_name` still resolves to a product server-side.
      const sres = await apiFetch('/api/v1/payments/checkout/session', {
        method: 'POST',
        // Only the CODE is sent. The server re-quotes it and returns the net in
        // `amount_krw` — never a price, a discount or a net from here.
        body: JSON.stringify({
          tier_name: selected.name.toLowerCase(),
          ...(appliedCouponCode ? { coupon_code: appliedCouponCode } : {}),
        }),
      });
      const session = await sres.json();
      if (!sres.ok) { setError(session.detail || 'Could not start checkout.'); return; }

      // 2. Open the KCP payment window.
      const PortOne = (await import('@portone/browser-sdk/v2')).default;
      const pay = await PortOne.requestPayment({
        storeId: session.store_id,
        channelKey: session.channel_key,
        paymentId: session.payment_id,
        orderName: session.order_name,
        totalAmount: session.amount_krw,
        currency: 'CURRENCY_KRW',
        // §10.2-A2 — the SELECTED method, not a hardcoded 'CARD'. Falls back to
        // CARD only if something set an unknown method, which cannot happen
        // through the UI but keeps the window openable rather than erroring.
        payMethod: PG_METHODS[payMethod] || 'CARD',
        // phoneNumber is MANDATORY for Inicis V2 일반결제 — without it PortOne's
        // prepare endpoint 400s before any window opens.
        customer: { ...(session.customer || {}), phoneNumber: PHONE_DIGITS(buyerPhone) },
        // N3.7 — KG Inicis uses a redirect-style window on mobile; without a
        // return target the buyer lands nowhere after paying. Send them back to
        // this page (the webhook still settles server-side either way).
        redirectUrl: `${window.location.origin}/profile/manage-membership`,
      });
      // requestPayment returns a `code` only on failure/cancel.
      if (pay && pay.code !== undefined) {
        // KG-inisys-checkout-error-9-20.md P2.2 — THE PG's OWN VERDICT IS IN
        // pgCode/pgMessage, NOT in code/message. PaymentResponse carries
        // {code, message, pgCode, pgMessage}; `code`/`message` are PortOne's
        // generic wrapper, while `pgMessage` holds KG Inicis's verbatim reason
        // and `pgCode` its error number, which maps straight into Inicis's
        // published error table. This branch read `pay.message` alone, so on
        // 2026-09-20 a card-company refusal ("PG Sub 사업자 번호 없음(안심클릭
        // 가맹점 오류)", Inicis 1200I8) existed nowhere but the buyer's
        // screenshot. The sibling catch below has logged its diagnostic since
        // 2026-08-23; this branch never got the same treatment.
        const pgDetail = [pay.pgCode, pay.pgMessage].filter(Boolean).join(' ');
        console.error('[checkout] portone failure', {   // eslint-disable-line no-console
          paymentId: session.payment_id,
          code: pay.code, message: pay.message,
          pgCode: pay.pgCode, pgMessage: pay.pgMessage,
        });

        // P2.1(c) — TELL THE SERVER THE ATTEMPT FAILED. Returning here without
        // this left the intent at 'created' and the coupon at 'reserved'
        // forever, so the buyer's NEXT attempt was refused by our own backend
        // with "This code isn't valid for this purchase." One failed card
        // payment burned their code.
        //
        // Safe: /checkout/complete re-fetches from PortOne and never trusts
        // this call — on a non-PAID verdict it records the status and releases
        // the hold. Best-effort on purpose: if it throws, the buyer must still
        // see why the PAYMENT failed, not why our bookkeeping call did.
        try {
          await apiFetchOk('/api/v1/payments/checkout/complete', {
            method: 'POST',
            body: JSON.stringify({ payment_id: session.payment_id }),
          });
        } catch (releaseErr) {
          console.error('[checkout] failure-notify failed', releaseErr);   // eslint-disable-line no-console
        }

        setError(
          (pay.message || (isKo ? '결제가 완료되지 않았습니다.' : 'Payment was not completed.'))
          + (pgDetail ? ` (${pgDetail})` : '')
        );
        return;
      }

      // 3. Server re-verifies with PortOne and activates the membership.
      const cres = await apiFetch('/api/v1/payments/checkout/complete', {
        method: 'POST',
        body: JSON.stringify({ payment_id: session.payment_id }),
      });
      const done = await cres.json();
      if (cres.ok && done.membership_active) {
        setResult({
          paid: true,
          tier_display_name: selected.display_name || selected.name,
          start_date: startDate, end_date: endDate, amount_due_krw: session.amount_krw,
        });
        setStep(STEP_DONE);
      } else {
        setError(done.detail || 'Payment received — activation is finishing. Please refresh shortly.');
      }
    } catch (e) {
      // Surface WHAT failed, not just THAT it failed. This catch used to swallow
      // the exception entirely and print a generic line, which made a live
      // checkout failure undiagnosable without a browser console — exactly the
      // situation on 2026-08-23, where the server showed a clean 200 on
      // /checkout/session and nothing after it, and the cause was invisible.
      // The PortOne SDK rejects (rather than returning a `code`) on config
      // errors — bad channelKey, a payMethod the channel does not support,
      // a blocked popup — so its message is the diagnostic.
      const detail = (e && (e.message || e.code)) ? `${e.code || ''} ${e.message || ''}`.trim() : '';
      console.error('[checkout] payment failed', e);   // eslint-disable-line no-console
      setError(
        (isKo ? '결제를 진행할 수 없습니다.' : 'Could not complete the payment.')
        + (detail ? ` (${detail})` : '')
        + (isKo ? ' 결제가 이루어진 경우 hello@whereat.kr 로 문의해 주세요.'
                : ' If you were charged, contact hello@whereat.kr.')
      );
    } finally {
      setLoading(false);
    }
    // `payMethod` is load-bearing here, not incidental: without it this closure
    // captures whatever method was selected when the callback was first created
    // and sends THAT to PortOne. A buyer who switched to 계좌이체 would get a
    // card window (§10.2-A2, the same misrouting in a different disguise).
  }, [selected, startDate, endDate, payMethod, buyerPhone, isKo, appliedCouponCode]);

  // ── Step: Select tier ─────────────────────────────────────────────────────
  if (step === STEP_SELECT) {
    return (
      <div className="mmpage">
        <div className="mmpage-header">
          <button className="mmpage-back" onClick={goBack}>← Back</button>
          {/* The ?mode=topup variant of this heading ("Top Up Credits", and a
              Korean subtitle about adding them) was deleted on 2026-10-03
              (Portone-KGinisys-Pricing-fix-10-2.md T1.5). Nothing has linked to
              it since 2026-09-03 and there is nothing to top up, but the URL
              still put a stored-value heading on the checkout screen for anyone
              who typed it. The heading is localized now, like the subtitle. */}
          <h1>{isKo ? '플랜 선택' : 'Select a Membership Plan'}</h1>
          <p className="mmpage-sub">
            {/* T1.3 — must not promise bank-transfer-only: card is the default
                whenever PG payment is enabled. When it is off, say nothing about
                method rather than naming the wrong one. */}
            {isKo ? '팀에 맞는 플랜을 선택하세요.' : 'Choose the plan that fits your team.'}
            {payCfg.card_enabled
              ? (isKo ? ' 신용카드·실시간 계좌이체로 즉시 결제하거나, 무통장 입금을 선택할 수 있습니다.'
                      : ' Pay instantly by card or real-time transfer, or choose manual deposit.')
              : (isKo ? ' 결제는 무통장 입금으로 진행됩니다.' : ' Payment is by bank transfer.')}
          </p>
        </div>

        {tiers.length === 0 && !error && <p className="mmpage-loading">Loading plans…</p>}
        {error && <p className="mmpage-error">{error}</p>}

        {currentMembership && (
          <div className="mmpage-current-plan">
            <div className="mmpage-current-plan-label">Current membership</div>
            <div className="mmpage-current-plan-row">
              <span className="mmpage-current-plan-name">{currentMembership.tier_display_name}</span>
              <span className={`mmpage-current-plan-status mmpage-status-${currentMembership.status}`}>
                {currentMembership.status}
              </span>
            </div>
            {currentMembership.start_date && (
              <div className="mmpage-current-plan-dates">
                📅 {currentMembership.start_date} → {currentMembership.end_date}
                {currentMembership.days_remaining > 0 && (
                  <span className="mmpage-current-plan-days"> · {currentMembership.days_remaining} days left</span>
                )}
              </div>
            )}
            <CancelSubscriptionPanel
              membership={currentMembership}
              onCancelled={(info) => setCurrentMembership(m => ({ ...m, ...info, auto_renew: false }))}
            />
          </div>
        )}

        {/* Retired tiers are filtered from the CHOOSER only. An org already on
            one keeps it until end_date (Q6, "allowed to run out") and still
            sees it in the "current membership" panel above. */}
        <div className="mmpage-tier-grid">
          {tiers.filter(tier => !RETIRED_TIERS.has(tier.name)).map(tier => (
            <div
              key={tier.name}
              className={`mmpage-tier-card${selected?.name === tier.name ? ' selected' : ''}`}
              onClick={() => handleTierClick(tier)}
            >
              <div className="mmpage-tier-name">{tier.display_name}</div>
              <div className="mmpage-tier-price">
                {priceOf(tier) > 0 ? formatKrw(monthlyRateOf(tier)) : tier.name === 'trial' ? 'Free' : 'Contact us'}
              </div>
              {/* SAME THREE LINES AS THE LANDING CARDS, IN THE SAME ORDER:
                  price, then what it includes each month, then how it is billed.
                  A buyer comparing plans here and on '/' must not have to work
                  out whether two differently-worded cards describe one product.

                  The ' / mo' suffix that used to sit on the price is gone, and
                  the billing note below replaces it. That is not a cosmetic
                  swap: '₩199,000 / mo' on a product charged ₩2,388,000 once, up
                  front, reads as a monthly subscription -- the defect logged as
                  A4 against the landing page, which is why PricingSection
                  carries a comment forbidding a bare monthly figure without the
                  annual qualifier. This screen had the bare figure AND no
                  qualifier, on the page where the money is actually committed.

                  The allowance is absent, not zero, for any tier without a plan
                  SKU (trial, enterprise): monthly_credits_for_tier returns None
                  for those and no anniversary grant ever runs, so there is no
                  monthly figure to state. */}
              {PLAN_MONTHLY_CREDITS[tier.name] != null && (
                <div className="mmpage-tier-credits">
                  {monthlyCreditsLine(PLAN_MONTHLY_CREDITS[tier.name], isKo)}
                </div>
              )}
              {/* D-2 (2026-10-03): the same note as the landing cards, without
                  "선불" -- PortOne lists 선불 서비스 as a high-risk category of
                  its own. English keeps "billed annually". */}
              {priceOf(tier) > 0 && (
                <div className="mmpage-tier-billing">
                  {isKo ? '월 환산 · 12개월 이용료 일괄 결제' : 'per month · billed annually'}
                </div>
              )}
              {tier.description && <div className="mmpage-tier-desc">{tier.description}</div>}
              {selected?.name === tier.name && (
                <div className="mmpage-tier-check">✓ Selected</div>
              )}
            </div>
          ))}
        </div>

        <div className="mmpage-actions">
          <button
            className="mmpage-btn-primary"
            disabled={!selected}
            onClick={() => setStep(STEP_DETAILS)}
          >
            Continue with {selected ? selected.display_name : '—'} →
          </button>
        </div>

        {/* N3.1 — the one-time "Buy a single report" card was removed: its
            /payments/checkout/report route is a hard 410 (packs are hidden, Q5),
            so it would show a price then hard-error once card payments turn on. */}

        {/* Tier change confirmation modal */}
        {pendingTier && (
          <div className="mmpage-modal-overlay" onClick={() => setPendingTier(null)}>
            <div className="mmpage-modal" onClick={e => e.stopPropagation()}>
              <h3>Change Membership Plan?</h3>
              <div className="mmpage-modal-compare">
                <div className="mmpage-modal-plan mmpage-modal-plan-old">
                  <div className="mmpage-modal-plan-label">Current selection</div>
                  <div className="mmpage-modal-plan-name">{selected.display_name}</div>
                  <div className="mmpage-modal-plan-price">{priceOf(selected) > 0 ? formatKrw(monthlyRateOf(selected)) + ' / mo' : 'Free'}</div>
                </div>
                <div className="mmpage-modal-arrow">→</div>
                <div className="mmpage-modal-plan mmpage-modal-plan-new">
                  <div className="mmpage-modal-plan-label">New selection</div>
                  <div className="mmpage-modal-plan-name">{pendingTier.display_name}</div>
                  <div className="mmpage-modal-plan-price">{priceOf(pendingTier) > 0 ? formatKrw(monthlyRateOf(pendingTier)) + ' / mo' : 'Free'}</div>
                </div>
              </div>
              <div className="mmpage-modal-billing">
                <div className="mmpage-modal-billing-row">
                  <span>12-month cost ({selected.display_name})</span>
                  {/* priceOf() is ALREADY the full 12-month prepaid total — do NOT multiply by 12. */}
                  <span className="old-price">{formatKrw(priceOf(selected))}</span>
                </div>
                <div className="mmpage-modal-billing-row">
                  <span>12-month cost ({pendingTier.display_name})</span>
                  <strong className="new-price">{formatKrw(priceOf(pendingTier))}</strong>
                </div>
                <div className="mmpage-modal-billing-due">
                  {/* 12-month 단건결제: the whole term total is charged at purchase, not a "first month". */}
                  {priceOf(pendingTier) > 0
                    ? <>Due today — full 12-month term, prepaid: <strong>{formatKrw(priceOf(pendingTier))}</strong></>
                    : <>✅ No payment required for Trial plan</>}
                </div>
              </div>
              <div className="mmpage-modal-actions">
                <button className="mmpage-btn-primary mmpage-btn-confirm" onClick={confirmTierChange}>
                  Yes, switch to {pendingTier.display_name}
                </button>
                <button className="mmpage-btn-secondary" onClick={() => setPendingTier(null)}>
                  Keep {selected.display_name}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Step: Date & pricing details ──────────────────────────────────────────
  if (step === STEP_DETAILS) {
    return (
      <div className="mmpage">
        <div className="mmpage-header">
          <button className="mmpage-back" onClick={() => setStep(STEP_SELECT)}>← Change plan</button>
          <h1>Membership Details</h1>
          <p className="mmpage-sub">
            Plan: <strong>{selected.display_name}</strong> &nbsp;·&nbsp; {tierPricePerMonth > 0 ? formatKrw(tierPricePerMonth) + ' / month' : 'Free'}
          </p>
        </div>

        <div className="mmpage-form-card">
          {/* ── SERVICE PERIOD IS FIXED, NOT CHOSEN ────────────────────────
              The two <input type="date"> pickers and the "Quick duration"
              buttons that used to sit here are GONE
              (PortOne-Payment-Refactor-8-14.md P2-T2.1/T2.3).

              KG Inicis merchant review flagged the calendar: each product must
              declare a fixed 사용기간 rather than letting the buyer pick one.
              The period is derived server-side from the SKU and echoed back on
              the checkout session; this is now display-only. */}
          <div className="mmpage-period-notice">
            <div className="mmpage-period-line">
              <strong>{isKo ? '이용기간' : 'Service period'}:</strong>{' '}
              {isKo ? '12개월 (구매일로부터)' : '12 months from purchase'}
            </div>
            {/* ABSENCE IS DATA. This read `TIER_MONTHLY_CREDITS[...] ?? 0`, which
                turned an unmapped or retired tier into the confident assertion
                "매월 0건" on the checkout screen -- telling a buyer about to pay
                2,388,000 won that the plan includes nothing. Every other surface
                in this change set renders nothing when the allowance is unknown
                (PricingSection omits the line, the settings row shows a dash);
                a zero is the one answer that is never right. Incident O3 was the
                same mistake in a different column: a 403 rendered as "0 credits".

                Rendered only when the selected tier HAS a known allowance. Trial
                and Enterprise have no plan SKU and no anniversary grant, so there
                is no monthly allowance to state and the line is absent.

                2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T1.5): this is
                the sentence a KG이니시스 reviewer reads directly above 결제하기,
                and it labelled the allowance as a stored value. It now names
                the service and its fixed quantity through the same helpers as
                '/' and '/pricing': the monthly line, then the 12-month total
                with its no-carry-over clause (the monthly grant is a hard
                reset, so the total is never shown without it). */}
            {PLAN_MONTHLY_CREDITS[selected?.name] != null && (
              <div className="mmpage-period-line">
                <strong>{isKo ? '제공 서비스' : 'Included service'}:</strong>{' '}
                {monthlyCreditsLine(PLAN_MONTHLY_CREDITS[selected.name], isKo)}
                {' · '}{planTermTotalLine(PLAN_MONTHLY_CREDITS[selected.name], isKo)}
              </div>
            )}
            <div className="mmpage-period-line mmpage-period-muted">
              {isKo
                ? '결제는 12개월 선결제 단건결제입니다. 자동 정기결제가 아닙니다.'
                : 'Billed once, 12 months prepaid. This is not a recurring subscription.'}
            </div>
          </div>
          {/* The "Quick duration" (+1mo / +3mo / +2 Years) buttons are gone with
              the calendar — the term is fixed at 12 months (P2-T2.1). */}

          <div className="mmpage-price-summary">
            <div className="mmpage-price-row">
              <span>{isKo ? '플랜' : 'Plan'}</span>
              <span><strong>{selected.display_name}</strong></span>
            </div>
            <div className="mmpage-price-row">
              <span>{isKo ? '월 환산' : 'Per month'}</span>
              <span>{priceKrw > 0 ? formatKrw(monthlyRateOf(selected)) + (isKo ? ' / 월' : ' / month') : 'Free'}</span>
            </div>
            <div className="mmpage-price-row">
              <span>{isKo ? '이용기간' : 'Service period'}</span>
              <span><strong>{isKo ? '12개월' : '12 months'}</strong></span>
            </div>
            {/* ── COUPON ──────────────────────────────────────────────────
                Shown for EVERY payment method: a coupon is a property of the
                purchase, not of how it is paid for, and the wire path takes one
                too (profile.py MembershipRequestBody). Hiding it for 무통장 입금
                would quietly deny the discount to whoever picks that button. */}
            {couponProductCode && priceKrw > 0 && (
              <div className="mmpage-coupon">
                <label className="mmpage-coupon-label" htmlFor="mmpage-coupon-input">
                  {t('coupon.label')}
                </label>
                {couponApplied ? (
                  <div className="mmpage-coupon-applied">
                    <span className="mmpage-coupon-applied-text">
                      {t('coupon.appliedCode', { code: couponApplied.coupon_code })}
                    </span>
                    <button
                      type="button"
                      className="mmpage-coupon-remove"
                      onClick={clearCoupon}
                    >
                      {t('coupon.remove')}
                    </button>
                  </div>
                ) : (
                  <div className="mmpage-coupon-row">
                    <input
                      id="mmpage-coupon-input"
                      className="mmpage-coupon-input"
                      type="text"
                      autoComplete="off"
                      spellCheck="false"
                      maxLength={64}
                      value={couponInput}
                      placeholder={t('coupon.placeholder')}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                    />
                    <button
                      type="button"
                      className="mmpage-coupon-apply"
                      disabled={couponBusy || !couponInput.trim()}
                      onClick={applyCoupon}
                    >
                      {couponBusy ? t('coupon.applying') : t('coupon.apply')}
                    </button>
                  </div>
                )}
                {/* The server's sentence, verbatim. See applyCoupon(). */}
                {couponNotice && (
                  <div className="mmpage-coupon-notice" role="status" aria-live="polite">
                    {couponNotice}
                  </div>
                )}
              </div>
            )}

            {/* Both numbers come from the SERVER quote, so the breakdown adds up
                to the amount that will actually be charged even if the local
                catalogue price has drifted from the catalogue on the server. */}
            {couponApplied && (
              <>
                <div className="mmpage-price-row">
                  <span>{t('coupon.listPrice')}</span>
                  <span>{formatKrw(couponApplied.list_amount_krw)}</span>
                </div>
                <div className="mmpage-price-row mmpage-price-discount">
                  <span>{t('coupon.discount', { code: couponApplied.coupon_code })}</span>
                  <span>−{formatKrw(couponApplied.discount_krw)}</span>
                </div>
              </>
            )}

            <div className="mmpage-price-row mmpage-price-total">
              <span>{isKo ? '총 결제금액 (12개월 선결제)' : 'Total (12 months, prepaid)'}</span>
              <span><strong>{formatKrw(payableKrw)}</strong></span>
            </div>
            {priceKrw === 0 && (
              <div className="mmpage-due-by mmpage-due-by-free">
                ✅ {isKo ? '체험 플랜 — 결제가 필요하지 않습니다' : 'Trial plan — no payment required'}
              </div>
            )}
            {/* ⚠ T0.4 — 청약철회 NOTICE AT THE POINT OF PURCHASE. NOT decoration.

                전자상거래법 제17조제6항 makes DISPLAYING the withdrawal restriction a
                PRECONDITION for the restriction binding. Undisplayed, the customer
                keeps the full unrestricted right and our "the current month is not
                refundable" term simply does not apply. Deleting this block does not
                tidy the checkout — it silently widens our refund liability.

                Text comes from constants/legal.js so it cannot drift from the terms
                page, the FAQ and the cancellation email. */}
            {priceKrw > 0 && (
              <div className="mmpage-refund-note">
                <strong>{isKo ? '청약철회 및 환불 안내' : 'Withdrawal and refund notice'}</strong>
                <p>{isKo ? CHECKOUT_WITHDRAWAL_NOTICE.ko : CHECKOUT_WITHDRAWAL_NOTICE.en}</p>
                <p className="mmpage-refund-note-link">
                  <Link to="/about#refund" target="_blank" rel="noopener noreferrer">
                    {isKo ? '환불 및 청약철회 정책 전문 보기' : 'Read the full refund and withdrawal policy'}
                  </Link>
                </p>
              </div>
            )}
          </div>

          {/* A3 — three DISTINCT labels (shared PaymentMethodOptions). 계좌이체
              and the manual 무통장 입금 both render as "bank transfer" in
              English, so collapsing them would show two options meaning
              different things under one name. showWire=true here (default):
              unlike packs, plans DO have the admin-confirmed wire path. */}
          {payCfg.card_enabled && tierPricePerMonth > 0 && (
            <PaymentMethodOptions isKo={isKo} payMethod={payMethod} setPayMethod={setPayMethod} />
          )}

          {payMethod === 'wire' && (
          <div className="mmpage-wire-box">
            <div className="mmpage-wire-title">💳 Wire Transfer Details</div>
            <div className="mmpage-wire-row">
              <span className="mmpage-wire-label">Bank</span>
              <span className="mmpage-wire-value">{WIRE.bank}</span>
            </div>
            <div className="mmpage-wire-row">
              <span className="mmpage-wire-label">Name</span>
              <span className="mmpage-wire-value">{WIRE.name}</span>
            </div>
            <div className="mmpage-wire-row">
              <span className="mmpage-wire-label">Account</span>
              <span className="mmpage-wire-value mmpage-wire-account">{WIRE.account}</span>
              <button
                className="mmpage-copy-btn"
                onClick={() => handleCopy(WIRE.account, 'account')}
              >
                {copied === 'account' ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            {/* payableKrw, not priceKrw: with a coupon applied the server files
                the request at the NET, so quoting the list price here would tell
                the buyer to transfer an amount no admin is going to match. */}
            <p className="mmpage-wire-note">
              Please transfer <strong>{formatKrw(payableKrw)}</strong> — the full 12-month term, prepaid.
              Your membership will be activated manually once payment is confirmed.
            </p>
          </div>
          )}

          {/* Inicis V2 일반결제 requires the buyer's mobile number (PortOne
              prepare 400s without it). Shown only for PG methods — the manual
              wire path does not go through PortOne and does not need one. */}
          {isPgMethod(payMethod) && (
            <BuyerPhoneField
              isKo={isKo}
              value={buyerPhone}
              onChange={setBuyerPhone}
              invalid={buyerPhone !== '' && !isValidKrMobile(buyerPhone)}
            />
          )}

          {isPgMethod(payMethod) && (
            <div className="mmpage-card-note">
              {isKo
                ? <>💳 안전한 결제창으로 이동합니다. 결제가 완료되면 멤버십이 <strong>즉시 활성화</strong>되며, 관리자 확인 절차가 없습니다.</>
                : <>💳 You'll be taken to a secure payment window. Your membership
                    activates <strong>instantly</strong> on success — no admin step.</>}
            </div>
          )}

          {error && <p className="mmpage-error">{error}</p>}

          <div className="mmpage-actions">
            {/* §10.2-A2 — isPgMethod, NOT `=== 'card'`. This ternary is the trap:
                anything not matching falls to the manual wire request, so a new
                PortOne method added to the chooser but not to PG_METHODS would
                silently file a wire request instead of opening a payment window. */}
            {isPgMethod(payMethod) ? (
              <button
                className="mmpage-btn-primary"
                disabled={loading || payableKrw < 1 || !isValidKrMobile(buyerPhone)}
                onClick={handleCardPayment}
              >
                {loading
                  ? (isKo ? '결제창 여는 중…' : 'Opening payment…')
                  : (isKo
                      ? `${formatKrw(payableKrw)} 결제하기`
                      : `Pay ${formatKrw(payableKrw)}`)}
              </button>
            ) : (
              <button
                className="mmpage-btn-primary"
                disabled={loading || payableKrw < 1}
                onClick={handleSubmit}
              >
                {loading ? 'Submitting…' : 'Submit Membership Request'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Step: Confirmation ────────────────────────────────────────────────────
  // Card path → payment already complete + membership active (no bank details).
  //
  // The credit-pack confirmation that preceded this branch was deleted on
  // 2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T3.1, D-8) -- see the
  // withdrawn-packs note near the top of this file.
  if (result?.paid) {
    return (
      <div className="mmpage">
        <div className="mmpage-done-icon">✅</div>
        <h1 className="mmpage-done-title">Payment Complete</h1>
        <p className="mmpage-done-sub">
          Your <strong>{result.tier_display_name}</strong> membership is now active.
          A receipt has been emailed to you.
        </p>
        <div className="mmpage-done-card">
          <h3>Payment Summary</h3>
          <div className="mmpage-price-row"><span>Plan</span><span>{result.tier_display_name}</span></div>
          <div className="mmpage-price-row"><span>Start</span><span>{result.start_date}</span></div>
          <div className="mmpage-price-row"><span>End</span><span>{result.end_date}</span></div>
          <div className="mmpage-price-row mmpage-price-total">
            <span>Paid</span><span><strong>{formatKrw(result.amount_due_krw)}</strong></span>
          </div>
        </div>
        <div className="mmpage-actions">
          <button className="mmpage-btn-primary" onClick={() => window.location.assign('/app')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mmpage">
      <div className="mmpage-done-icon">✅</div>
      <h1 className="mmpage-done-title">Request Submitted</h1>
      <p className="mmpage-done-sub">
        A confirmation email has been sent to your registered address.
      </p>

      {/* Part A: Summary */}
      <div className="mmpage-done-card">
        <h3>Membership Request Summary</h3>
        <div className="mmpage-price-row"><span>Plan</span><span>{result?.tier_display_name}</span></div>
        <div className="mmpage-price-row"><span>Start</span><span>{result?.start_date}</span></div>
        <div className="mmpage-price-row"><span>End</span><span>{result?.end_date}</span></div>
        <div className="mmpage-price-row mmpage-price-total">
          <span>Amount due</span><span><strong>{
            result?.amount_due_krw > 0
              ? formatKrw(result.amount_due_krw)
              : priceKrw > 0 ? formatKrw(priceKrw) : '₩0 (Free Trial)'
          }</strong></span>
        </div>
      </div>

      {/* Part B: Free trial notice.
          2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T1.5): it was
          English-only on a Korean screen, called the trial a stored value and
          hand-typed the 5. The quantity is TRIAL_SIGNUP_CREDITS -- "provided at
          signup", not "remaining": a trial org may already have used some. */}
      <div className="mmpage-pending-notice">
        {/* Copy status: pending_ceo (D9) -- the plan gives the paragraph below
            verbatim, not this Korean heading. */}
        <strong>{isKo ? '⏳ 멤버십 활성화 대기 중입니다.' : '⏳ Your membership is pending activation.'}</strong>
        <p>
          {isKo
            ? <>입금이 확인되기 전까지는 <strong>무료 체험(가입 시 제공된 분석 리포트 {TRIAL_SIGNUP_CREDITS}건)</strong>으로 VeriPass를 계속 이용하실 수 있습니다.</>
            : <>Until we confirm your transfer, you stay on the <strong>Free Trial ({TRIAL_SIGNUP_CREDITS} analysis reports provided at signup)</strong> and can keep exploring VeriPass.</>}
        </p>
      </div>

      {/* Part C: Payment instructions */}
      <div className="mmpage-wire-box">
        <div className="mmpage-wire-title">💳 Complete Your Payment</div>
        <div className="mmpage-wire-row">
          <span className="mmpage-wire-label">Bank</span>
          <span className="mmpage-wire-value">{WIRE.bank}</span>
        </div>
        <div className="mmpage-wire-row">
          <span className="mmpage-wire-label">Name</span>
          <span className="mmpage-wire-value">{WIRE.name}</span>
        </div>
        <div className="mmpage-wire-row">
          <span className="mmpage-wire-label">Account</span>
          <span className="mmpage-wire-value mmpage-wire-account">{WIRE.account}</span>
          <button
            className="mmpage-copy-btn"
            onClick={() => handleCopy(WIRE.account, 'done-account')}
          >
            {copied === 'done-account' ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <p className="mmpage-wire-note">
          Please transfer{' '}
          <strong>{result?.amount_due_krw != null ? formatKrw(result.amount_due_krw) : '—'}</strong>{' '}
          before <strong>{result?.start_date}</strong>.
          Questions? <a href="mailto:hello@whereat.kr">hello@whereat.kr</a>
        </p>
      </div>

      <div className="mmpage-actions">
        <button className="mmpage-btn-secondary" onClick={() => navigate('/profile')}>
          Go to Profile
        </button>
        <button className="mmpage-btn-secondary" onClick={() => navigate('/app')}>
          Start Exploring
        </button>
      </div>
    </div>
  );
}

/**
 * T3 (.claude/TODO/duplicate-refund-request-9-24.md) -- the server's own
 * sentence, in the reader's language. Never "[object Object]", never a code.
 *
 * `detail` is a STRING for our own HTTPExceptions but a LIST of objects for a
 * FastAPI request-validation failure -- which this call can genuinely provoke:
 * `contact_email` below is sent as `undefined` when the membership row has no
 * contact email, JSON.stringify drops the key, and the required field is then
 * missing. `String(detail)` over that list renders "[object Object]", which
 * swallows exactly the reason the block below exists to surface.
 *
 * T2 added `detail_ko` / `detail_en` alongside `detail` on the 409s. Preferring
 * the Korean one on a Korean screen is not us writing copy -- the server wrote
 * both sentences; it just stops a Korean customer reading an English refusal.
 *
 * ⚠ `body.error` is NOT a message fallback any more. Since T2 it is a stable
 * machine code ('duplicate_refund_request' / 'refund_not_available') meant for
 * branching, and printing it would put a symbol where a reason belongs.
 */
const serverDetail = (body, isKo) => {
  const localised = isKo ? body?.detail_ko : body?.detail_en;
  if (typeof localised === 'string' && localised.trim()) return localised.trim();
  const d = body?.detail;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (Array.isArray(d)) {
    const msgs = d.map((x) => (typeof x === 'string' ? x : x?.msg)).filter(Boolean);
    if (msgs.length) return msgs.join(' ');
  }
  return '';
};

// --------------------------------------------------------------------------
// CancelSubscriptionPanel — VeriPass Refund Policy
// The 12-month fee is charged once; refunds are pro-rata on unused whole months (N3.8).
// No plan auto-renews, so cancelling stops no renewal: access continues until end_date.
// --------------------------------------------------------------------------
export function CancelSubscriptionPanel({ membership, onCancelled }) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  // T18 — the live quote, fetched when the panel OPENS rather than on mount.
  // The customer must see the actual figure before confirming, not policy prose
  // about how a figure would be derived. `null` = not fetched; `false` = the
  // quote call failed, which is NOT the same as "nothing is refundable" and must
  // not be rendered as ₩0.
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!open || quote !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/v1/refunds/quote');
        const j = await res.json();
        if (!cancelled) setQuote(res.ok ? j : false);
      } catch {
        if (!cancelled) setQuote(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, quote]);

  const alreadyCancelled = membership?.auto_renew === false || membership?.cancellation_requested_at;

  if (alreadyCancelled && !done) {
    // A-03 class (Portone-KGinisys-Pricing-fix-10-2.md T2.10). This said
    // "Auto-renewal has been turned off", in English only, but no plan
    // auto-renews: the 12-month fee is charged once. It now says what the
    // cancellation email says (subscription_cancelled.html).
    return (
      <div className="mmpage-cancel-note">
        {isKo
          ? `구독이 해지되었습니다. ${membership.end_date || '현재 이용 기간 종료일'}까지 계속 서비스를 이용하실 수 있습니다.`
          : `Your plan has been cancelled. You can keep using the service until ${membership.end_date || 'the end of your current term'}.`}
      </div>
    );
  }

  if (done) {
    return (
      <div className="mmpage-cancel-note">
        {isKo
          ? `✓ 해지가 접수되었습니다. ${done.access_until || membership?.end_date}까지 이용 가능합니다.`
          : `✓ Cancellation scheduled. Access continues until ${done.access_until || membership?.end_date}.`}
        {/* The original complaint was that this message appeared and then
            "nothing happens about the refunds". Say what happened to the money. */}
        {done.refund_requested === true && (
          <div style={{ marginTop: 6 }}>
            {isKo ? '환불 요청이 접수되었습니다. 설정 › 결제에서 진행 상황을 확인하세요.'
                  : 'Your refund request has been filed. Track it under Settings › Payments.'}
          </div>
        )}
        {done.refund_requested === false && (
          <div style={{ marginTop: 6, color: '#92400E' }}>
            {/* T3 -- a 409 is a REFUSAL the customer can act on, not a breakage.
                The server's own sentence is printed verbatim below; the only
                thing written here is where to go next, and that differs by the
                T2 error code. An unrecognised 409 gets the neutral line -- it
                still sends them somewhere useful without asserting a reason we
                were not told. Everything else -- 5xx, a timeout, the rate limit,
                a dropped connection -- really is ours, and keeps support. */}
            {done.refund_status === 409
              ? done.refund_error_code === 'duplicate_refund_request'
                ? (isKo ? '해지는 완료되었습니다. 이 결제에 대한 환불 요청이 이미 접수되어 있어 새로 접수되지 않았습니다. 설정 › 결제에서 확인해 주세요.'
                        : 'Your subscription was cancelled. A refund request for this payment already exists, so a second one was not filed — see Settings › Payments.')
                : (isKo ? '해지는 완료되었습니다. 환불 요청은 접수되지 않았습니다. 설정 › 결제에서 환불 요청 내역을 확인해 주세요.'
                        : 'Your subscription was cancelled. The refund request was not filed — check your refund requests under Settings › Payments.')
              : (isKo ? '해지는 완료되었으나 환불 요청 접수에 실패했습니다. 지원팀에 문의해 주세요.'
                      : 'Your subscription was cancelled, but the refund request could not be filed. Please contact support.')}
            {/* The reason, verbatim from the server. Without it the customer and
                support both have to guess, and the most common cause is
                actionable by the customer: a live request for this payment
                already exists (migration 048 permits one), so the right advice
                is "check Settings › Payments", not "contact support". */}
            {done.refund_error && (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>
                {isKo ? '사유: ' : 'Reason: '}{String(done.refund_error)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await apiFetch('/api/v1/subscription/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || null, confirm: true }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed to cancel');
      }
      const j = await res.json();

      // T19 — cancelling and asking for the money back are ONE action for the
      // customer, so the request is filed here rather than left as a second
      // screen they have to discover. Deliberately after the cancel succeeded,
      // and deliberately non-fatal: the cancellation is real either way, and
      // failing the whole flow over the request would leave the customer unsure
      // which half happened. The quote is re-priced server-side from the month
      // count — the browser's figure is never trusted.
      if (quote && quote.refundable && quote.months_refunded > 0) {
        try {
          const rr = await apiFetch('/api/v1/refunds/request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              months: quote.months_refunded,
              reason: (reason && reason.trim().length >= 10)
                ? reason.trim()
                : 'Subscription cancelled by the customer.',
              contact_email: membership?.contact_email || undefined,
            }),
          });
          // ⚠ apiFetch DOES NOT THROW ON 4xx/5xx — it returns the response for
          // every status except 401. This block used to be
          //     await apiFetch(...); j.refund_requested = true;
          // so the flag was set unconditionally and the `catch` below only ever
          // fired on a network failure. A 409 (a live request already exists for
          // this payment — migration 048 enforces one) or a 500 left the customer
          // reading "환불 요청이 접수되었습니다" while nothing had been created,
          // and the failure message at the top of this file was unreachable.
          //
          // That is the "refund request does nothing" report: it was not silent,
          // it was reporting success.
          j.refund_requested = rr.ok;
          if (!rr.ok) {
            const body = await rr.json().catch(() => ({}));
            // T3 -- verbatim, and normalised so a validation-error LIST cannot
            // reach the screen as "[object Object]". `HTTP nnn` only when the
            // server sent no sentence at all.
            j.refund_error = serverDetail(body, isKo) || `HTTP ${rr.status}`;
            // T3 -- the STATUS and the T2 error CODE are carried alongside the
            // sentence, because the failures need different ADVICE and only the
            // advice is ours to write. A 409 is a refusal the customer can act
            // on from their own screens, so "contact support" is the wrong
            // instruction and always was -- the comment below the `done` branch
            // has said so since T19 while the code kept saying support anyway.
            //
            // ⚠ The code, never a guess. Two different things answer 409 here
            // ('duplicate_refund_request' and 'refund_not_available') and T2
            // added the code precisely so a client does not have to infer which
            // from the wording. Where the code is absent or unrecognised the
            // advice stays neutral rather than asserting one of the two (O3).
            j.refund_status = rr.status;
            j.refund_error_code = typeof body?.error === 'string' ? body.error : null;
            // Non-fatal by design: the cancellation above really did happen, and
            // failing the whole flow here would leave the customer unsure which
            // half went through. But it must be VISIBLE.
            console.warn('refund request failed', rr.status, j.refund_error);
          }
        } catch (e) {
          j.refund_requested = false;
          j.refund_error = e?.message || 'network error';
        }
      }

      setDone(j);
      onCancelled?.(j);
    } catch (e) {
      setErr(e.message || 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      {!open ? (
        <button
          type="button"
          className="mmpage-cancel-link"
          onClick={() => setOpen(true)}
        >
          Cancel subscription
        </button>
      ) : (
        <div className="mmpage-cancel-panel">
          <div className="mmpage-cancel-panel-title">
            {isKo ? '구독을 해지하시겠습니까?' : 'Cancel your subscription?'}
          </div>

          {/*
            T18 — THE FIGURE, NOT A DESCRIPTION OF HOW ONE IS DERIVED.
            This block used to hand-write: "unused whole months are refunded
            pro-rata (the month in progress and its credits are not). Cancelling
            stops the next charge." Both halves were wrong by 2026-09-11:
              • Q5 made the current month REFUNDABLE when no credits were
                consumed in it, so the parenthetical stated the opposite of the
                published policy.
              • "stops the next charge" describes a recurring charge that does
                not exist — every plan is 12 months prepaid.
            Hand-written refund terms are exactly what the single-source policy
            exists to prevent, so the prose now comes from REFUND_POLICY_SUMMARY
            and the numbers come from the live quote.
          */}
          <div className="mmpage-cancel-quote">
            {quote === null && (
              <div className="mmpage-cancel-quote-loading">
                {isKo ? '환불 금액을 계산하고 있습니다…' : 'Calculating your refund…'}
              </div>
            )}

            {/* A failed quote must never render as ₩0 — that reads as "you get
                nothing", which is a different statement from "we could not work
                it out". Block the confirm rather than guess. */}
            {quote === false && (
              <div className="mmpage-cancel-error">
                {isKo
                  ? '환불 금액을 불러오지 못했습니다. 잠시 후 다시 시도하거나 지원팀에 문의해 주세요.'
                  : 'We could not calculate your refund. Please try again shortly, or contact support.'}
              </div>
            )}

            {quote && quote.refundable && (
              <>
                <div className="mmpage-cancel-quote-head">
                  {isKo ? '환불 예정 금액' : 'You will be refunded'}
                </div>
                <div className="mmpage-cancel-quote-amount">{formatKrw(quote.refundable_krw)}</div>
                <div className="mmpage-cancel-quote-rows">
                  <div><span>{isKo ? '환불 개월 수' : 'Months refunded'}</span>
                       <span>{isKo ? `${quote.months_refunded}개월` : `${quote.months_refunded} months`}</span></div>
                  <div><span>{isKo ? '월 환산 요금' : 'Monthly rate'}</span>
                       <span>{formatKrw(quote.monthly_rate_krw)}</span></div>
                  <div><span>{isKo ? '이용 종료일' : 'Access ends'}</span>
                       <span>{String(quote.new_end_date || '').slice(0, 10)}</span></div>
                </div>

                {/* Q5 — the current month varies PER CUSTOMER now, so the reason
                    has to travel with the number. An unexplained deduction of a
                    month's fee is the single most disputed line on this screen.

                    2026-10-03 (Portone-KGinisys-Pricing-fix-10-2.md T2.6): the
                    count is of analysis reports used this month, and the refund
                    unit is the month's FEE -- never the reports themselves.
                    `current_month_credits_used` is the API field name and stays. */}
                {quote.current_month_refunded === false && (
                  <div className="mmpage-cancel-quote-note">
                    {isKo
                      ? `이번 달은 환불 대상이 아닙니다 (이번 달 분석 리포트 ${quote.current_month_credits_used ?? 0}건 사용).`
                      : `The current month is not refunded (${quote.current_month_credits_used ?? 0} analysis reports used this month).`}
                    {quote.current_month_determinable === false && (
                      <> {isKo ? '사용 내역을 확인하지 못해 보수적으로 계산되었으며, 검토 시 조정될 수 있습니다.'
                                : 'We could not confirm this month\u2019s usage, so it was calculated conservatively and may be adjusted on review.'}</>
                    )}
                  </div>
                )}
                {quote.current_month_refunded === true && (
                  <div className="mmpage-cancel-quote-note">
                    {isKo ? '이번 달에 제공된 분석 리포트를 사용하지 않으셔서 이번 달 이용료도 함께 환불됩니다.'
                          : 'The current month is included, because none of its analysis reports were used.'}
                  </div>
                )}
              </>
            )}

            {quote && !quote.refundable && (
              <div className="mmpage-cancel-quote-none">
                {isKo ? '환불 가능한 금액이 없습니다.' : 'There is nothing refundable on this plan.'}
                {quote.reason_en && (
                  <div className="mmpage-cancel-quote-note">
                    {isKo ? (quote.reason_ko || quote.reason_en) : quote.reason_en}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mmpage-cancel-panel-body">
            {isKo ? REFUND_POLICY_SUMMARY.ko : REFUND_POLICY_SUMMARY.en}
          </div>

          {/* T19 — confirming files a REQUEST; it does not move money. Saying so
              here is what stops "why hasn't it arrived" an hour later. */}
          {quote && quote.refundable && (
            <div className="mmpage-cancel-panel-body">
              {isKo
                ? '확인을 누르면 환불 요청이 접수됩니다. 담당자 확인 후 영업일 기준으로 처리되며, 결제 페이지에서 진행 상황을 확인하실 수 있습니다.'
                : 'Confirming files a refund request. It is reviewed and released on a business-day basis — you can follow its progress under Settings › Payments.'}
            </div>
          )}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional: tell us why (helps us improve)"
            maxLength={500}
            rows={2}
          />
          {err && <div className="mmpage-cancel-error">{err}</div>}
          <div className="mmpage-cancel-panel-actions">
            {/* Never let the customer confirm against a figure that is still
                loading or failed to load. A cancellation is reversible by
                support; a refund request filed against a number nobody saw is a
                dispute. */}
            <button
              type="button"
              disabled={busy || quote === null || quote === false}
              onClick={submit}
              className="mmpage-cancel-confirm-btn"
            >
              {busy
                ? (isKo ? '처리 중…' : 'Working…')
                : (isKo ? '해지 및 환불 요청' : 'Confirm cancellation')}
            </button>
            <button type="button" disabled={busy} onClick={() => setOpen(false)} className="mmpage-cancel-keep-btn">
              {isKo ? '구독 유지' : 'Keep subscription'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

